// The Customers page: a table of customers (with their hub allocations) + CRUD.
// Loads customers and hubs (hubs are needed for the allocation editor + labels).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, EmptyState, Input, Select, Spinner } from "../../components/ui";
import { fmtDate, fmtN } from "../../lib/format";
import { getErrorMessage } from "../../lib/errors";
import type { Customer, CustomerLane, Hub } from "../../lib/types";
import { listHubs } from "../hubs/hubsApi";
import { listAccountLanes } from "./lanesApi";
import { listCustomers, setCustomerActive } from "./customersApi";
import CustomerForm from "./CustomerForm";

const ALL = "__all__";

function tierLabel(tier: number): string {
  return tier === 1 ? "H2H lane" : "Direct";
}


export default function CustomerList() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [lanes, setLanes] = useState<Array<{ customerId: string; lane: CustomerLane }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // undefined = closed, null = creating, Customer = editing.
  const [formFor, setFormFor] = useState<Customer | null | undefined>(undefined);
  const [showInactive, setShowInactive] = useState(true);
  const [query, setQuery] = useState("");
  const [hubFilter, setHubFilter] = useState(ALL);

  // Label a hub by code (fallback to name) for the allocations column.
  const hubLabel = useMemo(() => {
    const m = new Map(hubs.map((h) => [h.id, h.code || h.name]));
    return (id: string) => m.get(id) ?? "?";
  }, [hubs]);

  // Per-customer load capacity by lane (load_capacity lives on customer_lanes).
  const lanesByCustomer = useMemo(() => {
    const m = new Map<string, Array<{ hubId: string; cap: number | null | undefined }>>();
    for (const { customerId, lane } of lanes) {
      const arr = m.get(customerId) ?? [];
      arr.push({ hubId: lane.hubId, cap: lane.loadCapacity });
      m.set(customerId, arr);
    }
    return m;
  }, [lanes]);

  const reload = useCallback(async () => {
    if (!accountId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [c, h, ln] = await Promise.all([listCustomers(accountId), listHubs(accountId), listAccountLanes(accountId)]);
      setCustomers(c);
      setHubs(h);
      setLanes(ln);
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to load customers."));
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function toggleActive(customer: Customer) {
    if (!accountId) return;
    try {
      await setCustomerActive(accountId, customer.id, !customer.active);
      await reload();
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to update customer."));
    }
  }

  const q = query.trim().toLowerCase();
  const visibleCustomers = customers.filter(
    (c) =>
      (showInactive || c.active) &&
      (q === "" || c.name.toLowerCase().includes(q) || (c.code ?? "").toLowerCase().includes(q)) &&
      (hubFilter === ALL || c.allocations.some((a) => a.hubId === hubFilter))
  );

  const capLabel = (id: string) => {
    const ls = lanesByCustomer.get(id);
    if (!ls || ls.length === 0) return "—";
    return ls.map((l) => `${hubLabel(l.hubId)} ${l.cap == null ? "—" : fmtN(l.cap)}`).join(" · ");
  };

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">Customers</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
          </label>
          <Button onClick={() => setFormFor(null)}>+ New customer</Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-64">
            <Input placeholder="Search customer name or code…" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <div className="w-48">
            <Select value={hubFilter} onChange={(e) => setHubFilter(e.target.value)}>
              <option value={ALL}>All hubs</option>
              {hubs.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </Select>
          </div>
          <span className="text-xs text-slate-400">
            {visibleCustomers.length} of {customers.length} customers
          </span>
        </div>
      </Card>

      <Card className="max-h-[70vh] overflow-auto p-0">
        {loading ? (
          <Spinner />
        ) : customers.length === 0 ? (
          <EmptyState
            title="No customers yet"
            hint="Add your first customer to start forecasting."
            action={<Button onClick={() => setFormFor(null)}>+ New customer</Button>}
          />
        ) : visibleCustomers.length === 0 ? (
          <EmptyState title="No customers match your filters" hint="Try a different search or hub." />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-white">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Code</th>
                <th className="px-5 py-3 font-medium">Tier</th>
                <th className="px-5 py-3 font-medium">Load cap</th>
                <th className="px-5 py-3 font-medium">Daily base</th>
                <th className="px-5 py-3 font-medium">Growth</th>
                <th className="px-5 py-3 font-medium">Allocations</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {visibleCustomers.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-3 font-medium text-slate-900">{c.name}</td>
                  <td className="px-5 py-3 text-slate-600">{c.code ?? "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{tierLabel(c.tier)}</td>
                  <td className="px-5 py-3 text-slate-600">{capLabel(c.id)}</td>
                  <td className="px-5 py-3 text-slate-600">{fmtN(c.dailyBase)}</td>
                  <td className="px-5 py-3 text-slate-600">{c.growth}%</td>
                  <td className="px-5 py-3 text-slate-600">
                    {c.allocations.length === 0
                      ? "—"
                      : c.allocations
                          .map((a) => {
                            const range =
                              a.effectiveDate && a.endDate
                                ? ` (${fmtDate(a.effectiveDate)}→${fmtDate(a.endDate)})`
                                : a.effectiveDate
                                  ? ` (from ${fmtDate(a.effectiveDate)})`
                                  : a.endDate
                                    ? ` (until ${fmtDate(a.endDate)})`
                                    : "";
                            return `${hubLabel(a.hubId)} ${a.percentage}%${range}`;
                          })
                          .join(" · ")}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        c.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {c.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => setFormFor(c)}>
                        Edit
                      </Button>
                      <Button variant="ghost" onClick={() => toggleActive(c)}>
                        {c.active ? "Pause" : "Activate"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {formFor !== undefined && (
        <CustomerForm
          key={formFor?.id ?? "new"}
          accountId={accountId}
          customer={formFor}
          hubs={hubs}
          existingCustomers={customers}
          onClose={() => setFormFor(undefined)}
          onSaved={() => {
            setFormFor(undefined);
            reload();
          }}
        />
      )}
    </div>
  );
}
